import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Form from '@components/forms/Form';
import {
  PhoneNumberFragment as PhoneNumber,
  PhoneVerificationVerifyDocument,
  PhoneVerificationVerifyMutation,
  PhoneVerificationVerifyMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';

type Client = {
  id: string;
  verificationPhoneNumber: PhoneNumber | null;
};

type ClientResponse = NonNullable<NonNullable<PhoneVerificationVerifyMutation['phoneVerificationVerify']>['client']>;

type FormData = {
  code: string;
};

type Props = {
  client: Client;
  onCancel?: () => void;
  onSuccess: (client: ClientResponse, jwt: string) => void;
};

const PhoneVerificationForm = ({ client, onCancel, onSuccess }: Props) => {
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createPhoneNumber] = useMutation<PhoneVerificationVerifyMutation, PhoneVerificationVerifyMutationVariables>(
    PhoneVerificationVerifyDocument
  );

  const schema = yup
    .object()
    .shape({
      code: yup
        .string()
        .length(6, intl.formatMessage({ id: 'errors.codeVerification.code.mustBeSixDigits' }))
        .required(intl.formatMessage({ id: 'errors.codeVerification.code.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: { code: '' },
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return createPhoneNumber({
      variables: form.getValues()
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.phoneVerificationVerify,
        intl,
        onSuccess: () => {
          if (response.data?.phoneVerificationVerify?.client && response.data.phoneVerificationVerify.session) {
            onSuccess(response.data.phoneVerificationVerify.client, response.data.phoneVerificationVerify.session.jwt);
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.codeVerification'
      });
    });
  }, [createPhoneNumber, form, intl, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input<FormData> autofocus control={form.control} error={form.formState.errors.code} name="code" />
      <FormButtons
        cancelKey={client.verificationPhoneNumber?.verified ? undefined : 'pages.client.account.twoFactor.setUpLater'}
        hookForm={form}
        onCancel={onCancel}
        saveKey="pages.client.account.twoFactor.verify"
        skipCancel={client.verificationPhoneNumber?.verified}
      />
    </Form>
  );
};

export default PhoneVerificationForm;
